import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../services/AuthContext'
import apiService from '../services/api'
import { EmmaBrandLink } from '../components/EmmaBrandLink'

const LoginPage: React.FC = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [loading, setLoading] = useState(false)
  
  // Use localStorage to persist error across re-renders
  const [displayError, setDisplayError] = useState(() => {
    return localStorage.getItem('loginError') || ''
  })


  // Extract machineId, client, and redirect from URL on mount (in case user was redirected here)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    
    // Extract client parameter (premiere or desktop) and store in localStorage
    const client = urlParams.get('client')
    if (client === 'premiere' || client === 'desktop') {
      localStorage.setItem('auth_client', client)
    }
    
    // Extract machineId parameter (from Premiere plugin) and store in localStorage
    const machineId = urlParams.get('machineId')
    if (machineId) {
      localStorage.setItem('auth_machineId', machineId)
    }
    
    // Extract redirect path (where to go after auth) and store in localStorage
    const redirect = urlParams.get('redirect')
    if (redirect) {
      localStorage.setItem('auth_redirect', redirect)
    }
    
    // Determine eventType: if redirect is /subscribe, it's an upgrade flow, otherwise it's sign-in
    const eventType = redirect === '/subscribe' ? 'upgrade' : 'signin'
    localStorage.setItem('auth_eventType', eventType)
  }, [])

  // Clear error on successful login or when component unmounts
  React.useEffect(() => {
    return () => {
      localStorage.removeItem('loginError')
    }
  }, [])

  const handleGoogleSignIn = () => {
    const googleAuthUrl = apiService.getGoogleAuthUrl()
    window.location.href = googleAuthUrl
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Clear any existing errors
    localStorage.removeItem('loginError')
    setDisplayError('')
    setLoading(true)

    try {
      // Use AuthContext login method
      await login(formData.email, formData.password)
      
      // Check if we have a redirect path stored (e.g., from upgrade flow)
      const redirect = localStorage.getItem('auth_redirect')
      if (redirect) {
        localStorage.removeItem('auth_redirect') // Clean up
        navigate(redirect)
      } else {
        navigate('/auth-redirect')
      }
    } catch (err: any) {
      // Extract error message from axios response or use fallback
      const errorMessage = err.response?.data?.message || err.message || 'Login failed'
      
      // Store error in localStorage to persist across re-renders
      localStorage.setItem('loginError', errorMessage)
      setDisplayError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div style={{ 
        background: 'white', 
        padding: '2rem', 
        borderRadius: '1rem', 
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <EmmaBrandLink />
        <h1 style={{ textAlign: 'center', marginBottom: '2rem', color: '#1a202c' }}>
          Sign In
        </h1>

        {displayError && (
          <div className="alert alert-error">
            {displayError}
          </div>
        )}
        

        {/* Google Sign In - Prioritized */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            type="button"
            onClick={handleGoogleSignIn}
            style={{
              width: '100%',
              padding: '0.875rem 1.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              background: 'white',
              color: '#374151',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f9fafb'
              e.currentTarget.style.borderColor = '#9ca3af'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'white'
              e.currentTarget.style.borderColor = '#d1d5db'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        {/* Divider */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          margin: '1.5rem 0',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
          <span style={{ padding: '0 1rem' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginBottom: '1rem' }}
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center' }}>
          <Link to="/forgot-password" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            Forgot your password?
          </Link>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <span style={{ color: '#6b7280' }}>Don't have an account? </span>
          <Link to="/signup" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            Sign up
          </Link>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
